import React, { FC, ReactNode, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import cn from '../../utils/classnames';
import ModalBody from './components/modal-body/modal-body';
import ModalFooter from './components/modal-footer/modal-footer';
import ModalHeader from './components/modal-header/modal-header';
import ModalTitle from './components/modal-title/modal-title';
import useCloseOnEscape from './hooks/use-close-on-escape';
import useEnforceFocus from './hooks/use-enforce-focus';
import useLockBodyScroll from './hooks/use-lock-body-scroll';
import useModalAppearingAnimation from './hooks/use-modal-appearing-animation';
import { ModalContext } from './hooks/use-modal-context';
import useOutsideClickAnimation from './hooks/use-outside-click-animation';
import './modal.scss';

const modalRoot = document.body;

export type ModalSize = 'sm' | 'lg' | 'xl';

type ModalProps = {
    show: boolean;
    centered?: boolean;
    enforceFocus?: boolean;
    backdrop?: boolean | 'static';
    onHide?: () => void;
    onClick?: (event: React.MouseEvent) => void;
    size?: ModalSize;
    className?: string;
    containerClassName?: string;
    dialogClassName?: string;
    contentClassName?: string;
    backdropClassName?: string;
    children?: ReactNode;
};

const BaseCustomModal = (props: ModalProps) => {
    const {
        show,
        centered,
        enforceFocus = true,
        backdrop = true,
        onHide = () => {},
        onClick,
        size,
        className,
        containerClassName,
        dialogClassName,
        contentClassName,
        backdropClassName,
        children
    } = props;

    const dialogRef = useRef<HTMLDivElement>(null);

    const contextValue = useMemo(
        () => ({
            onHide
        }),
        [onHide]
    );

    const { showDefaultAnimation: modalAppearingAnimation } = useModalAppearingAnimation(show);
    const { onBackdropClick, bouncingAnimation } = useOutsideClickAnimation(backdrop, onHide);

    useCloseOnEscape(onHide, show);
    useLockBodyScroll(show);
    useEnforceFocus(dialogRef, show, enforceFocus);

    if (!show) {
        return null;
    }

    return createPortal(
        <ModalContext.Provider value={contextValue}>
            <div
                className={cn('fade modal  base-modal-container', className, containerClassName, {
                    show
                })}
            >
                {backdrop && (
                    <div className={cn('fade modal-backdrop show', backdropClassName)} onClick={onBackdropClick} />
                )}

                {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                <div
                    ref={dialogRef}
                    className={cn('modal-dialog', dialogClassName, {
                        'appearing-animation': modalAppearingAnimation,
                        'bouncing-animation': bouncingAnimation,
                        'modal-dialog-centered': centered,
                        'modal-sm': size === 'sm',
                        'modal-lg': size === 'lg',
                        'modal-xl': size === 'xl'
                    })}
                    role='dialog'
                    aria-modal='true'
                    onClick={(e) => {
                        e.stopPropagation();
                        onClick && onClick(e);
                    }}
                    tabIndex={-1}
                >
                    <div className={cn('modal-content', contentClassName)}>{children}</div>
                </div>
            </div>
        </ModalContext.Provider>,
        modalRoot
    );
};

type ModalComponent = FC<ModalProps> & {
    Title: typeof ModalTitle;
    Header: typeof ModalHeader;
    Body: typeof ModalBody;
    Footer: typeof ModalFooter;
};

export const Modal = Object.assign(BaseCustomModal, {
    Title: ModalTitle,
    Header: ModalHeader,
    Body: ModalBody,
    Footer: ModalFooter
}) as ModalComponent;
